import React, { useCallback, useState, useEffect } from "react";
import Cropper from 'react-easy-crop';
import "./cropper.css";
import { getCroppedImg } from "./CroppedImage";

const CoverImageCropper = ({ closeCoverModal, selectedCoverPicFile, setSelectedCoverPicFile }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);

  // Create an object URL for the selected image and clean it up on unmount
  useEffect(() => {
    const objectUrl = URL.createObjectURL(selectedCoverPicFile);
    setImageSrc(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedCoverPicFile]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = useCallback(async () => {
    if (!croppedAreaPixels || !imageSrc) {
      console.error('Cropped area pixels or image source not available');
      return;
    }

    setLoading(true);
    try {
      const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels, 0.9); // Increased quality to 0.9
      setSelectedCoverPicFile(croppedFile);
      closeCoverModal();
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setLoading(false);
    }
  }, [croppedAreaPixels, imageSrc, setSelectedCoverPicFile, closeCoverModal]);

  return (
      <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50 p-4">
        <div className="crop-dialog relative w-full max-w-[600px] bg-white rounded-lg shadow-lg">
          <button onClick={closeCoverModal} className="crop-dialog-close absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl z-10 w-8 h-8 flex items-center justify-center">
            &times;
          </button>
          <div className="crop-container-main flex flex-col">
            <div className="crop-container-cover">
              {imageSrc && (
                  <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={5} // 1200 / 240 = 5
                      onCropChange={setCrop}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                  />
              )}
            </div>
            <div className="flex mt-4 justify-center items-center">
              <label htmlFor="zoom" className="me-2 sm:me-4 font-semibold font-poppins text-sm sm:text-base">Zoom</label>
              <input
                  id="zoom"
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="zoom-range w-[180px] sm:w-[300px]"
              />
            </div>
          </div>
          <div className="mx-3 sm:mx-[20px] flex flex-col-reverse sm:flex-row justify-between gap-2 sm:gap-4 pb-3 sm:pb-0">
            <button
                className={`text-white bg-blue-500 w-full sm:w-[200px] text-sm px-4 py-3 my-1 sm:my-2 rounded-lg ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-600 hover:shadow-md"}`}
                onClick={handleCrop}
                disabled={loading}
            >
              {loading ? "Processing..." : "Crop Image"}
            </button>
            <button
                className="text-white bg-blue-500 w-full sm:w-[200px] text-sm px-4 py-3 my-1 sm:my-2 rounded-lg hover:bg-blue-600 hover:shadow-md"
                onClick={closeCoverModal}
            >
              Close
            </button>
          </div>
        </div>
      </div>
  );
};

export default CoverImageCropper;
